import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/useAuthStore';
import { useBusinessStore } from '../store/useBusinessStore';
import {
  BarChart2, Building, DollarSign, ShieldCheck, 
  LayoutDashboard, Store, Package, 
  Activity, Calendar, Users, 
  CreditCard, MessageSquare, Megaphone, Lock, RefreshCw, Menu, X, Contact, MapPin, FileText, Receipt, Landmark, Wallet, KeyRound, HelpCircle, CalendarCheck, Send, Sparkles, Target
} from 'lucide-react';

import { ErrorBoundary } from '../components/ErrorBoundary';
import { isModuleEnabled } from '../utils/businessConfigHelpers';
import { botMode } from '../utils/botTestMode';
import { refreshApp } from '../utils/appUpdate';
import { useAppUpdate } from '../hooks/useAppUpdate';
import { GROUP_COLORS, buildMenu, isItemActive } from './menuConfig';

const ICONS = { BarChart2, DollarSign, LayoutDashboard, Store, Package, Calendar, Users, CreditCard, MessageSquare, Megaphone, Contact, MapPin, FileText, Receipt, Landmark, Wallet, HelpCircle, CalendarCheck, Send, Sparkles, Target };

export default function AppLayout() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { userRole, logout } = useAuthStore();
  const { masterDb, activeBusinessId, setActiveBusinessId } = useBusinessStore();
  const location = useLocation();
  const navigate = useNavigate();
  const updateAvailable = useAppUpdate();
  const currentPath = location.pathname;

  const activeBusiness = (Array.isArray(masterDb) ? masterDb : []).find(b => String(b.id) === String(activeBusinessId)) || masterDb?.[0];
  const isImpersonating = userRole === 'master' && activeBusinessId;

  const ACTIVE_CLASSES = 'bg-seryi-green/10 text-seryi-green border border-seryi-green/30 font-bold';
  const INACTIVE_CLASSES = 'text-seryi-text hover:text-seryi-heading hover:bg-seryi-bg-light/50';
  const isActive = (path) => (currentPath === path ? ACTIVE_CLASSES : INACTIVE_CLASSES);

  const btnClasses = "flex items-center gap-3 px-4 py-3 rounded-xl text-xs md:text-sm font-semibold transition-all duration-200 whitespace-nowrap w-full border border-transparent";
  const smallBtnClasses = "flex items-center justify-between px-3 py-2 rounded-xl text-xs font-semibold transition-all duration-200 whitespace-nowrap w-full border border-transparent";

  // Menú del negocio: 7 grupos (ver menuConfig.js); cada módulo apagado se oculta o se muestra con candado según el ítem.
  const isRestaurant = (activeBusiness?.tipo_negocio || '').toLowerCase().includes('restaurante');
  const menu = buildMenu((moduleKey) => isModuleEnabled(activeBusiness, moduleKey), { isRestaurant });

  return (
    <div className="h-screen overflow-hidden bg-seryi-bg-dark text-slate-100 flex flex-col md:flex-row font-sans relative">
      
      {/* 0. TOPBAR MÓVIL */}
      <div className="md:hidden bg-seryi-bg border-b border-seryi-bg-light p-4 flex items-center justify-between z-20">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-seryi-green rounded-lg flex items-center justify-center shadow-lg shadow-seryi-green/20">
            <BarChart2 className="text-seryi-bg-dark" size={16} />
          </div>
          <div>
            <h1 className="font-extrabold text-base leading-none tracking-tight text-seryi-heading">CRM</h1>
            <span className="text-[9px] text-seryi-green font-semibold uppercase tracking-wider">
              {userRole === 'master' && !isImpersonating ? 'Consola Master' : 'Consola de Negocio'}
            </span>
          </div>
        </div>
        <button 
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className="p-2 bg-seryi-bg-dark rounded-xl text-seryi-text hover:text-seryi-heading transition-colors"
        >
          {isSidebarOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* OVERLAY MÓVIL */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-30 md:hidden backdrop-blur-sm transition-opacity"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* 1. SIDEBAR GLOBAL */}
      <aside className={`fixed md:static inset-y-0 left-0 z-40 w-64 bg-seryi-bg border-r border-seryi-bg-light flex flex-col justify-between p-6 shrink-0 md:h-screen md:overflow-y-auto transition-transform duration-300 ease-in-out ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}>
        <div className="space-y-8">
          
          {/* Brand Header (Oculto en móvil porque está en el topbar) */}
          <div className="hidden md:flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-seryi-green rounded-xl flex items-center justify-center shadow-lg shadow-seryi-green/20">
                <BarChart2 className="text-seryi-bg-dark" size={20} />
              </div>
              <div>
                <h1 className="font-extrabold text-lg leading-none tracking-tight">CRM</h1>
                <span className="text-[10px] text-seryi-green font-semibold uppercase tracking-wider">
                  {userRole === 'master' && !isImpersonating ? 'Consola Master' : 'Consola de Negocio'}
                </span>
              </div>
            </div>
          </div>

          {/* Menú Dinámico según Rol */}
          <nav className="flex flex-col overflow-y-auto gap-1.5 pb-2 md:pb-0">
            {userRole === 'master' && !isImpersonating ? (
              <>
                <Link onClick={() => setIsSidebarOpen(false)} to="/master/dashboard" className={`${btnClasses} ${isActive('/master/dashboard')}`}>
                  <BarChart2 size={16} /> <span>Dashboard Central</span>
                </Link>
                <Link onClick={() => setIsSidebarOpen(false)} to="/master/clients" className={`${btnClasses} ${isActive('/master/clients')}`}>
                  <Building size={16} /> <span>Administrar Clientes</span>
                </Link>
                <Link onClick={() => setIsSidebarOpen(false)} to="/master/plans" className={`${btnClasses} ${isActive('/master/plans')}`}>
                  <DollarSign size={16} /> <span>Planes y Precios</span>
                </Link>
                <Link onClick={() => setIsSidebarOpen(false)} to="/master/profile" className={`${btnClasses} ${isActive('/master/profile') || isActive('/master/vault')}`}>
                  <ShieldCheck size={16} /> <span>Bóveda de Accesos & Demos</span>
                </Link>
              </>
            ) : (
              <>
                {isImpersonating ? (
                  <div className="pb-3 border-b border-indigo-900/50 hidden md:block bg-seryi-bg-light/20 p-2.5 rounded-xl border">
                    <label className="text-[10px] text-seryi-green font-bold uppercase tracking-wider block mb-1 flex items-center gap-1">
                      <ShieldCheck size={12} /> Impersonando:
                    </label>
                    <select 
                      className="w-full bg-seryi-bg-dark border border-indigo-500/40 rounded-lg px-2 py-1.5 text-xs text-seryi-heading focus:outline-none"
                      value={activeBusinessId}
                      onChange={(e) => setActiveBusinessId(e.target.value)}
                    >
                      {masterDb.map(b => (
                        <option key={b.id} value={b.id}>{(b.activo ? '🟢 ' : '🔴 ') + b.nombre_negocio}</option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div className="pb-3 border-b border-seryi-bg-light hidden md:block">
                    <span className="text-[10px] text-seryi-text font-bold uppercase tracking-wider block">Mi Comercio:</span>
                    <p className="text-xs font-black text-seryi-heading truncate">{activeBusiness?.nombre_negocio || 'Cargando...'}</p>
                  </div>
                )}

                {menu.map((group) => {
                  const color = GROUP_COLORS[group.color] || GROUP_COLORS.green;
                  return (
                    <div key={group.id} className={group.label ? 'pt-2' : ''}>
                      {group.label && (
                        <span className={`text-[10px] uppercase font-extrabold px-2 tracking-wider flex items-center gap-1.5 mb-1 ${color.title}`}>
                          {group.emoji} {group.label}
                        </span>
                      )}
                      <div className={group.label ? `space-y-1 ml-2 pl-2 border-l ${color.line}` : ''}>
                        {group.items.map((item) => {
                          const Icon = ICONS[item.icon];
                          const active = isItemActive(item, currentPath);
                          const cls = group.label ? smallBtnClasses : btnClasses;
                          return (
                            <Link key={item.to} onClick={() => setIsSidebarOpen(false)} to={item.to}
                              className={`${cls} ${active ? color.active : `text-seryi-text ${color.hover}`} ${item.locked ? 'opacity-80' : ''}`}>
                              <div className="flex items-center gap-2.5">
                                <Icon size={group.label ? 15 : 16} className={color.icon} /> <span>{item.label}</span>
                              </div>
                              {item.locked && <Lock size={12} className="text-amber-400/90" title="Requiere activar este módulo" />}
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </nav>
        </div>

        {/* Footer Sidebar (User profile/Logout) */}
        <div className="pt-4 border-t border-seryi-bg-light mt-auto">
          {/* Legal links */}
          <div className="flex items-center justify-center gap-3 mb-3 text-[10px] text-seryi-text">
            <a href="https://seryiads.com/privacidad" target="_blank" rel="noopener noreferrer" className="hover:text-seryi-green transition-colors">Privacidad</a>
            <span className="text-seryi-bg-light">·</span>
            <a href="https://seryiads.com/terminos" target="_blank" rel="noopener noreferrer" className="hover:text-seryi-green transition-colors">Términos</a>
          </div>
          {isImpersonating && (
            <button 
              onClick={() => {
                setActiveBusinessId(null);
                navigate('/master/dashboard');
              }}
              className="w-full mb-3 flex justify-center items-center gap-2 bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30 px-3 py-2 rounded-xl text-xs font-bold transition-colors border border-indigo-500/30"
            >
              Regresar a Master
            </button>
          )}
          
          <Link
            to="/v2/cuenta"
            onClick={() => setIsSidebarOpen(false)}
            className="w-full mb-3 flex justify-center items-center gap-2 bg-seryi-bg-light/30 text-seryi-text hover:text-seryi-heading hover:bg-seryi-bg-light/60 px-4 py-2.5 rounded-xl text-xs font-bold transition-colors"
          >
            <KeyRound size={14} /> Mi cuenta y contraseña
          </Link>
          <button 
            onClick={logout}
            className="w-full mb-3 flex justify-center items-center gap-2 bg-rose-500/10 text-rose-500 hover:bg-rose-500/20 px-4 py-2.5 rounded-xl text-sm font-bold transition-colors"
          >
            Cerrar Sesión
          </button>
          <button 
            onClick={refreshApp}
            className="w-full flex justify-center items-center gap-2 bg-slate-800/40 text-slate-400 hover:text-white hover:bg-slate-700/50 px-4 py-2 rounded-xl text-xs font-bold transition-colors"
          >
            <RefreshCw size={14} /> Resetear Caché
          </button>
        </div>
      </aside>

      {/* 2. MAIN CONTAINER */}
      <main className="flex-1 p-6 md:p-10 space-y-8 overflow-y-auto">
        {updateAvailable && (
          <button type="button" onClick={refreshApp} className="w-full text-left rounded-2xl border border-seryi-green/40 bg-seryi-green/10 px-4 py-2.5 text-xs text-seryi-heading hover:bg-seryi-green/15">
            <strong>Hay una versión nueva de la app.</strong> Pulsa aquí para actualizar (se recarga la página).
          </button>
        )}
        {botMode(activeBusiness) === 'prueba' && (
          <Link to="/config" className="block rounded-2xl border border-amber-500/40 bg-amber-500/10 px-4 py-2.5 text-xs text-amber-200 hover:bg-amber-500/15">
            <strong>Bot en modo prueba:</strong> solo responde a tus números de prueba. Cuando esté afinado, enciéndelo para todos en Configuración.
          </Link>
        )}
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </main>

    </div>
  );
}

